import { useRef } from 'react';
import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
  useReducedMotion,
} from 'framer-motion';
import { STACK_GROUPS } from '../constants';
import { SectionHead } from './Services';

const wrap = (min, max, v) => {
  const r = max - min;
  return ((((v - min) % r) + r) % r) + min;
};

function VelocityRow({ items, baseVelocity = 2, outline = false }) {
  const reduce = useReducedMotion();
  const x = useMotionValue(0);
  const { scrollY } = useScroll();
  const v = useVelocity(scrollY);
  const smooth = useSpring(v, { damping: 50, stiffness: 400 });
  const factor = useTransform(smooth, [0, 1000], [0, 4], { clamp: false });
  const dir = useRef(1);
  const tx = useTransform(x, (val) => `${wrap(-50, 0, val)}%`);

  useAnimationFrame((_, delta) => {
    if (reduce) return;
    const f = factor.get();
    if (f < -0.05) dir.current = -1;
    else if (f > 0.05) dir.current = 1;
    const move = baseVelocity * (delta / 1000) * (1 + Math.abs(f));
    x.set(x.get() + move * dir.current);
  });

  const run = [...items, ...items];
  return (
    <div className="overflow-hidden mask-fade-x">
      <motion.ul style={{ x: tx }} className="flex w-max whitespace-nowrap">
        {run.map((name, i) => (
          <li
            key={`${name}-${i}`}
            aria-hidden={i >= items.length}
            className={`display px-5 text-5xl sm:text-7xl lg:text-[6.5rem] leading-[1.1] ${
              outline ? 'outline-type' : i % 3 === 1 ? 'text-accent' : 'text-ink'
            }`}
          >
            {name}
          </li>
        ))}
      </motion.ul>
    </div>
  );
}

export default function TechStack({ full = false, hideHead = false }) {
  const all = STACK_GROUPS.flatMap((g) => g.items);
  const half = Math.ceil(all.length / 2);

  return (
    <section id="techstack" aria-labelledby="stack-title" className={`relative ${hideHead ? 'pb-24 sm:pb-32' : 'py-24 sm:py-32'}`}>
      {hideHead ? (
        <h2 id="stack-title" className="sr-only">
          Technologies we use
        </h2>
      ) : (
        <div className="frame">
          <SectionHead id="stack-title" title="The stack">
Proven tools, so your product is easy to grow.
          </SectionHead>
        </div>
      )}

      <div className={`${hideHead ? '' : 'mt-14'} space-y-2 sm:space-y-4`}>
        <VelocityRow items={all.slice(0, half)} baseVelocity={-2} />
        <VelocityRow items={all.slice(half)} baseVelocity={2} outline />
      </div>

      {full && (
      <div className="frame mt-16">
        <dl className="grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-5">
          {STACK_GROUPS.map((g) => (
            <div key={g.name} className="bg-base p-6">
              <dt className="text-sm text-accent">{g.name}</dt>
              <dd className="mt-3 space-y-1 text-ink">
                {g.items.map((it) => (
                  <span key={it} className="block">
                    {it}
                  </span>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      </div>
      )}
    </section>
  );
}
